import {
  ChangeEvent,
  DragEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type { UploaderProps, FileError } from "./types";
import { UploaderErrors } from "./constants";
import { validateAcceptedFile } from "./utils";

/**
 * Custom hook for managing file uploads, including validation, processing, and error handling.
 * This is used internally by the Uploader component to manage the state and lifecycle of uploaded files.
 * @template ProcessedFile The type of the processed file after upload.
 */
export function useFileUploader<ProcessedFile = File>({
  multiple = false,
  onChange,
  accepts: acceptsProp,
  maxFileSize = Number.MAX_VALUE,
  processFile,
  skipFileRead = false,
  inputRef
}: UseFileUploaderProps<ProcessedFile>) {
  const [isEmpty, setIsEmpty] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [uploadedFiles, setUploadedFiles] = useState<Map<string, File>>(
    new Map()
  );
  const [fileErrors, setFileErrors] = useState<Map<string, FileError>>(
    new Map()
  );
  const [successfullyProcessedFiles, setSuccessfullyProcessedFiles] = useState<
    Map<string, { file: File; processed: ProcessedFile }>
  >(new Map());

  const successFiles = useMemo(
    () =>
      new Map<string, File>(
        Array.from(successfullyProcessedFiles).map(([key, value]) => [
          key,
          value.file
        ])
      ),
    [successfullyProcessedFiles]
  );

  const [processingFiles, setProcessingFiles] = useState<Map<string, File>>(
    new Map<string, File>()
  );

  // Stable identity when the caller omits `accepts`, so downstream memoized
  // callbacks (onFileUpload, openFilePicker) don't churn every render.
  const accepts = useMemo(() => acceptsProp ?? [], [acceptsProp]);

  const acceptedExtensions = useMemo(
    () => accepts.filter(str => str[0] === "."),
    [accepts]
  );
  const acceptedTypes = useMemo(
    () => accepts.filter(str => str[0] !== "."),
    [accepts]
  );

  /**
   * Returns a copy of the set in the previous file state with the provided file removed.
   * @param file The file to remove from the set.
   * @param currentFileState The current file state.
   * @returns the updated file state with the target file removed.
   */
  const deleteFromFileSet = <
    T extends File | { file: File; processed: ProcessedFile }
  >(
    id: string,
    currentFileState: Map<string, T>
  ) => {
    const nextFileState = new Map(Array.from(currentFileState));
    nextFileState.delete(id);
    return nextFileState;
  };

  const updateProcessingFiles = (id: string) => {
    setProcessingFiles((currentProcessingFiles: Map<string, File>) =>
      deleteFromFileSet(id, currentProcessingFiles)
    );
  };

  const handleOnChange = useCallback(
    (
      nextSuccessFiles: Map<string, { file: File; processed: ProcessedFile }>,
      errorFiles: Map<string, FileError>
    ) => {
      onChange(
        new Map(
          Array.from(nextSuccessFiles).map(([_id, value]) => [
            value.file.name,
            value.processed
          ])
        ),
        errorFiles
      );
    },
    [onChange]
  );

  const onDelete = useCallback(
    (id: string) => {
      const newUploadedFiles = deleteFromFileSet(id, uploadedFiles);
      const newSuccessFiles = deleteFromFileSet(id, successfullyProcessedFiles);
      const newProcessingFiles = deleteFromFileSet(id, processingFiles);
      const newFileErrors = new Map(Array.from(fileErrors));
      if (newFileErrors.has(id)) {
        newFileErrors.delete(id);
      }
      setUploadedFiles(newUploadedFiles);
      setFileErrors(newFileErrors);
      setSuccessfullyProcessedFiles(newSuccessFiles);
      setProcessingFiles(newProcessingFiles);
      if (
        newUploadedFiles.size !== uploadedFiles.size ||
        newFileErrors.size !== fileErrors.size ||
        newProcessingFiles.size !== processingFiles.size ||
        newSuccessFiles.size !== successfullyProcessedFiles.size
      ) {
        handleOnChange(newSuccessFiles, newFileErrors);
      }
    },
    [
      uploadedFiles,
      successfullyProcessedFiles,
      processingFiles,
      fileErrors,
      handleOnChange
    ]
  );

  const onFileUpload = useCallback(
    (
      source: FileList | File[] | null | DragEvent<HTMLDivElement>,
      reselectId?: string
    ) => {
      const fileList =
        source instanceof FileList || source instanceof Array
          ? source
          : source?.dataTransfer?.files;
      if (!fileList) {
        return;
      }

      setIsEmpty(!fileList.length);

      const nextUploadedFiles = new Map<string, File>(
        multiple ? Array.from(uploadedFiles) : []
      );
      const nextFileErrors = new Map<string, FileError>(
        multiple ? Array.from(fileErrors) : []
      );

      const nextProcessingFiles = new Map<string, File>(
        multiple ? Array.from(processingFiles) : []
      );

      const nextSuccessFiles = new Map(
        Array.from(multiple ? successfullyProcessedFiles : [])
      );

      if (reselectId) {
        nextUploadedFiles.delete(reselectId);
        nextFileErrors.delete(reselectId);
      }

      for (let i = 0; i < (multiple ? fileList.length : 1); i++) {
        const id = crypto.randomUUID();
        if (
          accepts.length &&
          !validateAcceptedFile(fileList[i], {
            extensions: acceptedExtensions,
            types: acceptedTypes
          })
        ) {
          nextFileErrors.set(id, {
            file: fileList[i],
            error: UploaderErrors.UNSUPPORTED_FILE_TYPE
          });
        } else if (fileList[i].size > maxFileSize) {
          nextFileErrors.set(id, {
            file: fileList[i],
            error: `${UploaderErrors.FILE_SIZE} of ${maxFileSize} bytes`
          });
        } else {
          nextProcessingFiles.set(id, fileList[i]);
          nextUploadedFiles.set(id, fileList[i]);
        }
      }

      setUploadedFiles(nextUploadedFiles);
      setFileErrors(nextFileErrors);
      setProcessingFiles(nextProcessingFiles);
      setSuccessfullyProcessedFiles(nextSuccessFiles);
      setIsDragging(false);
    },
    [
      multiple,
      uploadedFiles,
      fileErrors,
      processingFiles,
      successfullyProcessedFiles,
      accepts,
      acceptedExtensions,
      acceptedTypes,
      maxFileSize
    ]
  );

  const onFileDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    onFileUpload(event);
  };

  // The transient input's native `change` listener fires later, so point it at
  // the latest handler to avoid acting on a stale state snapshot.
  const onFileUploadRef = useRef(onFileUpload);
  onFileUploadRef.current = onFileUpload;

  // Set just before opening the dialog so the input's `change` handler can
  // route a reselected file back to the id it replaces.
  const pendingReselectId = useRef<string | undefined>(undefined);

  // Opens the native file dialog against the rendered input when the caller
  // provides one (so external tools can drive it by dispatching `change`
  // directly), falling back to a transient input for consumers with no
  // rendered element (e.g. the dropzone hook).
  const openFilePicker = useCallback(
    (reselectId?: string) => {
      const existing = inputRef?.current;
      if (existing) {
        pendingReselectId.current = reselectId;
        // Reset so re-selecting the same file still fires `change`.
        existing.value = "";
        existing.multiple = multiple && !reselectId;
        existing.click();
        return;
      }

      const input = document.createElement("input");
      input.type = "file";
      if (accepts.length) {
        input.accept = accepts.join(",");
      }
      input.multiple = multiple && !reselectId;
      input.addEventListener("change", () =>
        onFileUpload(input.files, reselectId)
      );
      input.click();
    },
    [inputRef, multiple, accepts, onFileUpload]
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onFileUpload(event.target.files, pendingReselectId.current);
    pendingReselectId.current = undefined;
  };

  const onReselect = useCallback(
    (id: string) => {
      if (id) {
        openFilePicker(id);
      }
    },
    [openFilePicker]
  );

  async function handleFileProcessing(id: string, file: File): Promise<void> {
    if (processFile) {
      try {
        const processed = await processFile(file);
        setSuccessfullyProcessedFiles(prev =>
          new Map(Array.from(prev)).set(id, {
            file,
            processed
          })
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        setFileErrors(prev =>
          new Map(Array.from(prev)).set(id, {
            file,
            error: `${UploaderErrors.PROCESSING_ERROR}\n${errorMessage}`
          })
        );
      }
    } else {
      setSuccessfullyProcessedFiles(previousSuccessFiles =>
        new Map(Array.from(previousSuccessFiles)).set(id, {
          file,
          processed: file as unknown as ProcessedFile
        })
      );
    }
    updateProcessingFiles(id);
  }

  const clearFiles = useCallback(() => {
    const newSuccessFiles = new Map();
    const newFileErrors = new Map<string, FileError>();
    setIsEmpty(true);
    setUploadedFiles(new Map<string, File>());
    setFileErrors(newFileErrors);
    setSuccessfullyProcessedFiles(newSuccessFiles);
    setProcessingFiles(new Map<string, File>());
    handleOnChange(newSuccessFiles, newFileErrors);
  }, [handleOnChange]);

  useEffect(() => {
    if (
      !uploadedFiles.size &&
      !processingFiles.size &&
      !fileErrors.size &&
      !successfullyProcessedFiles.size
    ) {
      setIsEmpty(true);
      return;
    }
    if (!processingFiles.size && !isEmpty) {
      handleOnChange(successfullyProcessedFiles, fileErrors);
    }
  }, [uploadedFiles, processingFiles, fileErrors, successfullyProcessedFiles]);

  useEffect(() => {
    if (!processingFiles.size) {
      return;
    }

    processingFiles.forEach((file: File, id: string) => {
      if (skipFileRead) {
        // Skip reading the entire file into memory — pass it directly to
        // successFiles. Used for large file uploads where the caller handles
        // its own validation (e.g., chunked CSV validation).
        handleFileProcessing(id, file);
        return;
      }

      const reader = new FileReader();
      reader.readAsText(file);
      reader.onerror = event => {
        setFileErrors(previousErrors =>
          new Map<string, FileError>(Array.from(previousErrors)).set(id, {
            file,
            error: `${UploaderErrors.READING_ERROR}\n${JSON.stringify(event)}`
          })
        );
        updateProcessingFiles(id);
      };
      reader.onload = () => {
        handleFileProcessing(id, file);
      };
    });
  }, [uploadedFiles, successfullyProcessedFiles, fileErrors]);

  return {
    isEmpty,
    isDragging,
    uploadedFiles,
    successFiles,
    fileErrors,
    successfullyProcessedFiles,
    processingFiles,
    acceptedExtensions,
    acceptedTypes,
    deleteFromFileSet,
    updateProcessingFiles,
    handleOnChange,
    onDelete,
    onFileUpload,
    onFileDrop,
    openFilePicker,
    handleInputChange,
    onReselect,
    setIsEmpty,
    setFileErrors,
    setSuccessfullyProcessedFiles,
    setProcessingFiles,
    setUploadedFiles,
    setIsDragging,
    clearFiles,
    handleFileProcessing
  };
}

export type UseFileUploaderProps<ProcessedFile> = Pick<
  UploaderProps<ProcessedFile>,
  | "multiple"
  | "onChange"
  | "accepts"
  | "maxFileSize"
  | "processFile"
  | "skipFileRead"
> & {
  /**
   * When provided, the file dialog is opened against this rendered input so
   * external tooling (e2e, RTL) can drive uploads by dispatching `change`.
   * Omit it to synthesize a transient input per open (e.g. the dropzone hook).
   */
  inputRef?: RefObject<HTMLInputElement>;
};
